import { Component, Input } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { AppConstants } from 'src/app/utilities/constant';
import { ToastService } from 'src/app/services/toast.service';
import { BackendCallService } from 'src/app/services/backend-call/backend-call.service';
import { ShareValuesService } from 'src/app/services/sharedValues/share-values.service';
import { UserAuthService } from 'src/app/services/user-auth/user-auth.service';
import { TranslateService } from '@ngx-translate/core';
import { ImageUploadEvent } from 'src/app/shared/components/image-upload/image-upload.component';
import { UploadConfirmationModalComponent, UploadConfirmationResult } from 'src/app/components/upload-confirmation-modal/upload-confirmation-modal.component';
import { ImageEditorService } from 'src/app/services/image-editor/image-editor.service';

@Component({
  selector: 'app-pictogram-modal',
  templateUrl: './pictogram-modal.component.html',
  styleUrls: ['./pictogram-modal.component.scss'],
})
export class PictogramModalComponent {
  private readonly allowedImageTypes = [
    'image/jpeg',
    'image/jpg',
    'image/gif',
    'image/png',
    'image/webp',
    'image/bmp',
    'image/tga',
  ];

  @Input() isEditMode: boolean = false;
  @Input() categoryData: any;
  public pictogramcategoriesData: any;
  @Input() mode: 'category' | 'images' = 'category';
  public totalFiles: number = 0;
  public uploadProgress: number = 0;
  public failedFiles: string[] = [];
  public categoryName: string = '';
  public selectedCategory: any;
  public selectedFiles: File[] = [];
  public uploadedImages: any[] = [];
  public uploadedCount = 0;
  private imageFiles: File[];
  private pendingNextOrderNo: number = 0;
  /** Maximum allowed pictogram image file size (bytes). */
  private readonly MAX_PICTOGRAM_IMAGE_SIZE = 5242880; // 5 MB
  /** Temporarily holds validated files awaiting user confirmation. */
  private pendingFiles: File[] = [];
  constructor(
    public translate: TranslateService,
    public backendService: BackendCallService,
    private modalCtrl: ModalController,
    public sharedValue: ShareValuesService,
    private toastService: ToastService,
    public userAuthService: UserAuthService,
    private imageEditorService: ImageEditorService,
  ) {}

  /**
   * Fetches categories data and sets up edit mode if applicable.
   */
  ngOnInit() {
    this.getCategoriesData();
    if (this.isEditMode && this.categoryData) {
      this.categoryName = this.categoryData.name;
    }
  }

  /**
   * Fetches pictogram categories data from the backend.
   */
  private getCategoriesData() {
    this.backendService.getPictogramCategories().subscribe(
      (res: any) => {
        if (res?.response) {
          this.pictogramcategoriesData = res.response;
        }
      },
      (err) => {
        this.toastService.presentToast(
          err.error?.message,
          3000,
          AppConstants.ERROR_SNACKBAR,
          'top',
        );
      },
    );
  }

  /**
   * Closes the modal without saving changes.
   */
  close() {
    this.modalCtrl.dismiss();
  }

  /**
   * Updates the category name if it has changed and meets validation criteria.
   */
  public updateCategory() {
    const newName = this.categoryName?.trim() || '';
    const originalName = this.categoryData?.name?.trim() || '';

    // Check if anything changed (ignoring spaces)
    if (newName === originalName) {
     this.modalCtrl.dismiss();
      return;
    }

    // Check if empty
    if (newName.length === 0) {
      this.toastService.presentToast(
        'Category name cannot be empty',
        3000,
        AppConstants.WARNING_SNACKBAR,
        'top',
      );
      return;
    }

    this.modalCtrl.dismiss({
      id: this.categoryData.id,
      categoryName: newName,
    });
  }
  // Drag & Drop

  /**
   * Handles the drop event for drag-and-drop file uploads.
   * Validates files, prevents duplicates, and starts the upload process.
   */
  public onDrop(event: DragEvent) {
    event.preventDefault();

    const files = event.dataTransfer?.files;
    if (!files || files.length === 0) return;

    let duplicateFiles: string[] = [];

    Array.from(files).forEach((file: File) => {
      // MAGE VALIDATION
      if (!this.allowedImageTypes.includes(file.type)) {
        this.toastService.presentToast(
          this.translate.instant('please_upload_images_only'),
          3000,
          AppConstants.WARNING_SNACKBAR,
          'top',
        );
        return;
      }

      // PREVENT DUPLICATES
      const alreadyExists = this.selectedFiles.some(
        (f) =>
          f.name === file.name &&
          f.size === file.size &&
          f.lastModified === file.lastModified,
      );

      if (alreadyExists) {
        duplicateFiles.push(file.name);
        return;
      }

      this.selectedFiles.push(file);
    });

    // UPDATE TOTAL COUNT
    this.totalFiles = this.selectedFiles.length;

    //  DUPLICATE TOAST
    if (duplicateFiles.length === 1) {
      this.toastService.presentToast(
        `${duplicateFiles[0]}` + this.translate.instant('already_exists'),
        2500,
        AppConstants.WARNING_SNACKBAR,
        'top',
      );
    } else if (duplicateFiles.length > 1) {
      this.toastService.presentToast(
        `${duplicateFiles.length}` +
          this.translate.instant('images_already_exist_and_were_skipped'),
        3000,
        AppConstants.WARNING_SNACKBAR,
        'top',
      );
    }

    this.startUpload();
  }

  // File Select

  /**
   * Handles file selection from the file input.
   * Validates files, prevents duplicates, and starts the upload process.
   */
  public onFileSelected(event: any) {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    let duplicateFiles: string[] = [];
    Array.from(files).forEach((file: File) => {
      // IMAGE VALIDATION
      if (!this.allowedImageTypes.includes(file.type)) {
        this.toastService.presentToast(
          'Please upload image only',
          3000,
          AppConstants.WARNING_SNACKBAR,
          'top',
        );
        return;
      }

      //PREVENT DUPLICATES
      const alreadyExists = this.selectedFiles.some(
        (f) =>
          f.name === file.name &&
          f.size === file.size &&
          f.lastModified === file.lastModified,
      );

      if (alreadyExists) {
        duplicateFiles.push(file.name);
        return;
      }
      this.selectedFiles.push(file);
    });

    // UPDATE TOTAL COUNT (DON’T RESET)
    this.totalFiles = this.selectedFiles.length;
    event.target.value = null;
    // CONTINUE UPLOAD FOR NEW FILES ONLY
    if (duplicateFiles.length === 1) {
      this.toastService.presentToast(
        `${duplicateFiles[0]}` + this.translate.instant('already_exists'),
        2500,
        AppConstants.WARNING_SNACKBAR,
        'top',
      );
    } else if (duplicateFiles.length > 1) {
      this.toastService.presentToast(
        `${duplicateFiles.length}` +
          this.translate.instant('images_already_exist_and_were_skipped'),
        3000,
        AppConstants.WARNING_SNACKBAR,
        'top',
      );
    }
    this.startUpload();
  }

  /**
   * Handles files selected from the shared image upload component.
   * Validates files then shows the upload confirmation popup.
   */
  public onPictogramImagesSelected(event: ImageUploadEvent) {
    if (!this.selectedCategory) {
      this.toastService.presentToast(
        this.translate.instant('please_select_category'),
        3000,
        AppConstants.WARNING_SNACKBAR,
        'top',
      );
      return;
    }

    const files = event?.files;
    if (!files || files.length === 0) {
      return;
    }
    this.handleIncomingFiles(files);
  }

  /**
   * Validates incoming files (type + size + duplicates) and triggers the
   * upload-confirmation popup instead of uploading immediately.
   */
  private handleIncomingFiles(files: File[]) {
    const validTypeFiles = files.filter((file) =>
      this.allowedImageTypes.includes(file.type),
    );

    if (validTypeFiles.length !== files.length) {
      this.toastService.presentToast(
        this.translate.instant('please_upload_images_only'),
        3000,
        AppConstants.WARNING_SNACKBAR,
        'top',
      );
    }

    if (validTypeFiles.length === 0) {
      return;
    }

    // Size filter
    const validSizeFiles = validTypeFiles.filter(
      (file) => file.size <= this.MAX_PICTOGRAM_IMAGE_SIZE,
    );
    const oversizedCount = validTypeFiles.length - validSizeFiles.length;
    if (oversizedCount > 0) {
      const maxMB = (this.MAX_PICTOGRAM_IMAGE_SIZE / (1024 * 1024)).toFixed(0);
      this.toastService.presentToast(
        this.translate.instant('image_size_exceeds_limit', {
          count: oversizedCount,
          max: maxMB,
        }),
        4000,
        AppConstants.WARNING_SNACKBAR,
        'top',
      );
    }

    if (validSizeFiles.length === 0) {
      return;
    }

    // Deduplicate against already-queued files
    const duplicates: string[] = [];
    const newFiles: File[] = [];
    validSizeFiles.forEach((file) => {
      const exists = this.selectedFiles.some(
        (f) =>
          f.name === file.name &&
          f.size === file.size &&
          f.lastModified === file.lastModified,
      );
      if (exists) {
        duplicates.push(file.name);
      } else {
        newFiles.push(file);
      }
    });

    if (duplicates.length === 1) {
      this.toastService.presentToast(
        `${duplicates[0]}` + this.translate.instant('already_exists'),
        2500,
        AppConstants.WARNING_SNACKBAR,
        'top',
      );
    } else if (duplicates.length > 1) {
      this.toastService.presentToast(
        `${duplicates.length}` +
          this.translate.instant('images_already_exist_and_were_skipped'),
        3000,
        AppConstants.WARNING_SNACKBAR,
        'top',
      );
    }

    if (newFiles.length === 0) {
      return;
    }

    this.pendingFiles = newFiles;
    this.showPictogramUploadConfirmation();
  }

  /**
   * Shows the upload confirmation popup.
   * - Upload Directly: skip editor and start upload.
   * - Edit Images: open image editor first, then upload.
   * - Close (X): discard pending files.
   */
  private async showPictogramUploadConfirmation(): Promise<void> {
    if (!this.pendingFiles.length) {
      return;
    }

    const modal = await this.modalCtrl.create({
      component: UploadConfirmationModalComponent,
      cssClass: 'upload-confirmation-modal',
      backdropDismiss: false,
      componentProps: {
        imageCount: this.pendingFiles.length,
      },
      showBackdrop: true,
    });
    await modal.present();

    const { data } = await modal.onDidDismiss<UploadConfirmationResult>();
    const action = data?.action ?? 'cancel';

    if (action === 'cancel') {
      this.pendingFiles = [];
      return;
    }

    if (action === 'upload') {
      this.queueAndUpload(this.pendingFiles);
      this.pendingFiles = [];
      return;
    }

    if (action === 'edit') {
      const filesToEdit = [...this.pendingFiles];
      this.pendingFiles = [];

      const editedFiles: File[] = await this.imageEditorService.openBatch(
        filesToEdit,
        { exportFormat: 'jpeg', exportQuality: 0.92 },
      );

      if (editedFiles.length === 0) {
        return;
      }

      // Re-validate size after editing
      const validSizeEdited = editedFiles.filter(
        (file) => file.size <= this.MAX_PICTOGRAM_IMAGE_SIZE,
      );
      const oversizedEditedCount = editedFiles.length - validSizeEdited.length;
      if (oversizedEditedCount > 0) {
        const maxMB = (this.MAX_PICTOGRAM_IMAGE_SIZE / (1024 * 1024)).toFixed(
          0,
        );
        this.toastService.presentToast(
          this.translate.instant('image_size_exceeds_limit', {
            count: oversizedEditedCount,
            max: maxMB,
          }),
          4000,
          AppConstants.WARNING_SNACKBAR,
          'top',
        );
      }

      if (validSizeEdited.length === 0) {
        return;
      }

      this.queueAndUpload(validSizeEdited);
    }
  }

  /**
   * Pushes files into selectedFiles and kicks off the upload.
   */
  private queueAndUpload(files: File[]) {
    files.forEach((file) => this.selectedFiles.push(file));
    this.totalFiles = this.selectedFiles.length;
    this.startUpload();
  }

  /**
   * Prevents the default behavior for drag over events to allow dropping.
   */
  allowDrop(event: DragEvent) {
    event.preventDefault();
  }

  /**
   * Returns the selected category id regardless of whether ng-select stores
   * a full object or a primitive id.
   */
  private getSelectedCategoryId(): any {
    if (!this.selectedCategory) {
      return null;
    }

    if (typeof this.selectedCategory === 'object') {
      return this.selectedCategory?.id;
    }

    return this.selectedCategory;
  }

  /**
   * Returns selected category object regardless of whether selectedCategory
   * is a full object or only an id.
   */
  private getSelectedCategoryDetails(): any {
    if (!this.selectedCategory) {
      return null;
    }

    if (typeof this.selectedCategory === 'object') {
      return this.selectedCategory;
    }

    return this.pictogramcategoriesData?.find(
      (category: any) => category?.id === this.selectedCategory,
    );
  }

  /**
   * Returns the next available order number for a new image
   * within the currently selected category.
   */
  private getNextAvailableOrderNo(): number {
    // Get all images from the selected category (fallback to empty array)
    const selectedCategoryDetails = this.getSelectedCategoryDetails();
    const allImages = selectedCategoryDetails?.images || [];

    // Extract valid order numbers (supports both 'orderNo' and 'orderno')
    const orderNumbers = allImages
      .map((img) => img.orderNo || img.orderno || 0)
      .filter((num) => num > 0);

    // Determine the highest existing order number
    const highestOrder =
      orderNumbers.length > 0 ? Math.max(...orderNumbers) : 0;

    // Return next available order number
    return highestOrder + 1;
  }

  /**
   * Initiates the upload process for selected files.
   * Handles progress, success, and error scenarios.
   */
  private startUpload() {
    let remainingFiles = this.selectedFiles.slice(this.uploadedCount);
    if (!remainingFiles || remainingFiles.length === 0) return;

    // Reset error tracking
    this.failedFiles = [];

    // Assign files for shared method
    this.imageFiles = remainingFiles;

    // FILE PATH
    const file_path = `PictogramImages/`;

    const formData = new FormData();

    // Shared utility
    const hazardImageFormData = this.sharedValue.prepareImageFormData(
      formData,
      this.imageFiles,
      file_path,
    );

    //  ORDER NUMBER LOGIC
    const startingOrderNo =
      this.pendingNextOrderNo > 0
        ? this.pendingNextOrderNo + 1
        : this.getNextAvailableOrderNo();
    const numberOfFiles = this.imageFiles.length;
    this.pendingNextOrderNo = startingOrderNo + numberOfFiles - 1;
    let nextOrderNo = startingOrderNo;
    let processedFilesCount = 0;

    //  PROGRESS BAR START
    let progress = 0;

    const interval = setInterval(() => {
      if (progress < 90) {
        progress += 5;
        this.uploadProgress = progress;
      }
    }, 300);

    // API CALL
    this.backendService.uploadImages(hazardImageFormData).subscribe({
      next: (res: any[] | any) => {
        const responseItems = Array.isArray(res) ? res : [res];

        responseItems.forEach((element) => {
          const file = remainingFiles[processedFilesCount];
          if (!file) {
            return;
          }

          // HANDLE FAILED FILE (partial failure)
          if (!element || !element.url) {
            this.failedFiles.push(file.name);
            processedFilesCount++;
            return;
          }

          // SUCCESS CASE
          const imageData = {
            categoryid: this.getSelectedCategoryId(),
            filename: `LIB_Image_${nextOrderNo}`,
            filesize: element?.file_size,
            url: element?.url,
            thumbnailurl: element?.url,
            createdby: this.userAuthService.getUserList().userId,
            orderno: nextOrderNo,
          };

          this.uploadedImages.push(imageData);

          nextOrderNo++;
          processedFilesCount++;
        });
      },

      complete: () => {
        // STOP PROGRESS
        clearInterval(interval);
        this.uploadProgress = 100;

        // REMOVE FAILED FILES FROM QUEUE
        if (this.failedFiles.length > 0) {
          this.selectedFiles = this.selectedFiles.filter(
            (f) => !this.failedFiles.includes(f.name),
          );
        }

        // UPDATE COUNTS
        this.uploadedCount = this.uploadedImages.length;
        this.totalFiles = this.selectedFiles.length;

        // RESET pending order tracker
        this.pendingNextOrderNo = 0;

        // USER FEEDBACK
        if (this.failedFiles.length > 0) {
          this.toastService.presentToast(
            `${this.failedFiles.length}` +
              this.translate.instant('files_failed_to_upload'),
            3000,
            AppConstants.WARNING_SNACKBAR,
            'top',
          );
        }
      },

      error: (err) => {
        clearInterval(interval);

        // FORCE COMPLETE (UX FIX)
        this.uploadProgress = 100;

        //  ALL FILES FAILED
        this.failedFiles = remainingFiles.map((f) => f.name);

        // REMOVE FAILED FILES (CRITICAL FIX)
        this.selectedFiles = this.selectedFiles.filter(
          (f) => !this.failedFiles.includes(f.name),
        );

        // RESET STATE
        this.uploadedCount = this.uploadedImages.length;
        this.totalFiles = this.selectedFiles.length;
        this.pendingNextOrderNo = 0;

        //  SINGLE TOAST (avoid duplicates)
        this.toastService.presentToast(
          err?.error?.message || this.translate.instant('upload_failed'),
          3000,
          AppConstants.ERROR_SNACKBAR,
          'top',
        );
      },
    });
  }

  /**
   * Submits the modal data based on the current mode (category or images).
   * Performs validation and dismisses the modal with the appropriate data.
   */
  public submit() {
    if (this.mode === 'images') {
      if (!this.selectedCategory && this.selectedFiles.length === 0) {
        this.toastService.presentToast(
          this.translate.instant('please_select_category_and_upload_image'),
          3000,
          AppConstants.WARNING_SNACKBAR,
          'top',
        );
        return;
      }
      // Check category
      if (!this.selectedCategory) {
        this.toastService.presentToast(
          this.translate.instant('please_select_category'),
          3000,
          AppConstants.WARNING_SNACKBAR,
          'top',
        );
        return;
      }

      // Check files
      if (this.selectedFiles.length === 0) {
        this.toastService.presentToast(
          this.translate.instant('please_upload_at_least_one_image'),
          3000,
          AppConstants.WARNING_SNACKBAR,
          'top',
        );
        return;
      }

      // Check upload completed
      if (this.uploadedCount !== this.totalFiles) {
        this.toastService.presentToast(
          this.translate.instant('please_wait_until_all_images_are_uploaded'),
          3000,
          AppConstants.WARNING_SNACKBAR,
          'top',
        );
        return;
      }

      // Check failed files
      if (this.failedFiles.length > 0) {
        this.toastService.presentToast(
          `${this.failedFiles.length}` +
            this.translate.instant(
              'images_failed_to_upload_please_re_upload_and_try_again',
            ),
          3000,
          AppConstants.WARNING_SNACKBAR,
          'top',
        );
        return;
      }

      // All good — reset first then dismiss
      const selectedCategoryId = this.getSelectedCategoryId();
      const imageDataToSubmit = this.uploadedImages.map((image) => ({
        ...image,
        categoryid: selectedCategoryId,
      }));
      this.selectedFiles = [];
      this.uploadedImages = [];
      this.uploadedCount = 0;
      this.totalFiles = 0;
      this.uploadProgress = 0;
      this.failedFiles = [];

      this.modalCtrl.dismiss({
        ImageData: imageDataToSubmit,
      });

      return;
    }

    if (this.mode === 'category') {
      if (!this.categoryName?.trim()) {
        this.toastService.presentToast(
          this.translate.instant('please_enter_category_name'),
          3000,
          AppConstants.WARNING_SNACKBAR,
          'top',
        );
        return;
      }

      this.modalCtrl.dismiss({
        categoryName: this.categoryName.trim(),
      });
    }
  }
}
